import { AfterViewInit, Directive, ElementRef, Injector, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudItemComponent, UserStorage, requiredValidator } from '@scisys-uim/uim-common';
import { AssetsUser, BUSINESS_UNIT_METADATA, BUSINESS_UNIT_TYPE, BusinessUnit, FIREARM_STORAGE_LOCATION_TYPE, FirearmStorageLocation, RefCode, setDefaultLocationFromUser } from 'assets-model';
import { BusinessUnitService, FirearmStorageLocationService, RefCodeStorage } from 'assets-service';


@Directive()
export abstract class FirearmStorageLocationItemComponentBase extends CrudItemComponent<FirearmStorageLocation, number> implements OnInit, AfterViewInit {

  protected systemConfig: any;
  
  public form: FormGroup;
  
  businessUnitSelectionList: BusinessUnit[];

  locationTypeSelectionList: RefCode[];


  get eAssetsUser(): AssetsUser {
    return AssetsUser.assignAs(this.userDetails);
  }

  constructor(protected firearmStorageLocationService: FirearmStorageLocationService,
    protected businessUnitService: BusinessUnitService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(FIREARM_STORAGE_LOCATION_TYPE, fb, elementRef, router, route, userStorage, dialog, firearmStorageLocationService, injector, snackbar);
  }

  createNewItem() {
    return new FirearmStorageLocation();
  }
  
  createForm(): FormGroup {
    return this.fb.group({
      firearmStorageLocationId: [null, []],
      businessUnit: [null, []],
      addr1: [null, [requiredValidator,]],
      addr2: [null, []],
      addr3: [null, []],
      county: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      description: [null, [requiredValidator,]],
      disposalLocation: [null, [requiredValidator,]],
      email: [null, []],
      location: [null, [requiredValidator,]],
      locationType: [null, [requiredValidator,]],
      lockVersion: [null, []],
      postcode: [null, [requiredValidator,]],
      town: [null, []],
      updatedBy: [null, []],
      updatedDate: [null, []]
    });
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }
  
  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
  }
  
  protected initSystemConfig(config: any): any {
    config = config || {};
    return config;
  }

  protected initConfig(config: any) {
    super.initConfig(config);
    this.systemConfig = this.initSystemConfig(config);
  }
  
  setDefaults() {
    if (!this.readOnly && this.createMode) {
      setDefaultLocationFromUser(this.userDetails, this.item);

      // re-trigger updating drop-downs.
      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    if (!this.createMode) {
      this.firearmStorageLocationId?.disable();
    }
  }
  
  firearmStorageLocationIdChanging(value: number, unsetDependents: boolean = true) {}
  
  firearmStorageLocationIdChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.firearmStorageLocationIdChangedByUser(value, unsetDependents);
    }
  }  
  
  firearmStorageLocationIdChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  businessUnitDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }
  
  businessUnitLoadSelectionList() {
    this.businessUnitService.findAll().subscribe(
      data => {
        this.businessUnitSelectionList = BUSINESS_UNIT_TYPE.entityDecorator.ensureContained(this.item && this.item.businessUnit, data.items);
      }
    );
  }

  businessUnitChanging(value: BusinessUnit, unsetDependents: boolean = true) {}
  
  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.businessUnitChangedByUser(value, unsetDependents);
    }
  }  
  
  businessUnitChangedByUser(value: BusinessUnit, unsetDependents: boolean = true) {
  }
  
  businessUnitCompare(a: any, b: any) {
    return BUSINESS_UNIT_METADATA.equals(a, b);
  }
  
  addr1Changing(value: string, unsetDependents: boolean = true) {}
  
  addr1Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr1ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr1ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr2Changing(value: string, unsetDependents: boolean = true) {}
  
  addr2Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr2ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr2ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  addr3Changing(value: string, unsetDependents: boolean = true) {}
  
  addr3Changed(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.addr3ChangedByUser(value, unsetDependents);
    }
  }  
  
  addr3ChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  countyChanging(value: string, unsetDependents: boolean = true) {}
  
  countyChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.countyChangedByUser(value, unsetDependents);
    }
  }  
  
  countyChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdByChanging(value: string, unsetDependents: boolean = true) {}
  
  createdByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdByChangedByUser(value, unsetDependents);
    }
  }  
  
  createdByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  createdDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  createdDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.createdDateChangedByUser(value, unsetDependents);
    }
  }  
  
  createdDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  
  descriptionChanging(value: string, unsetDependents: boolean = true) {}
  
  descriptionChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.descriptionChangedByUser(value, unsetDependents);
    }
  }  
  
  descriptionChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  disposalLocationChanging(value: boolean, unsetDependents: boolean = true) {}
  
  disposalLocationChanged(value: boolean, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.disposalLocationChangedByUser(value, unsetDependents);
    }
  }  
  
  disposalLocationChangedByUser(value: boolean, unsetDependents: boolean = true) {
  }
  
  emailChanging(value: string, unsetDependents: boolean = true) {}
  
  emailChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.emailChangedByUser(value, unsetDependents);
    }
  }  
  
  emailChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  locationChanging(value: string, unsetDependents: boolean = true) {}
  
  locationChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.locationChangedByUser(value, unsetDependents);
    }
  }  
  
  locationChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  locationTypeLoadSelectionList() {
    this.refCodeStorage.getDomainElements('FIREARM_STORAGE_LOCATION_TYPE').subscribe(
      data => {
        this.locationTypeSelectionList = data;
      }
    );
  }

  locationTypeChanging(value: string, unsetDependents: boolean = true) {}
  
  locationTypeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.locationTypeChangedByUser(value, unsetDependents);
    }
  }  
  
  locationTypeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  lockVersionChanging(value: number, unsetDependents: boolean = true) {}
  
  lockVersionChanged(value: number, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.lockVersionChangedByUser(value, unsetDependents);
    }
  }  
  
  lockVersionChangedByUser(value: number, unsetDependents: boolean = true) {
  }
  
  postcodeChanging(value: string, unsetDependents: boolean = true) {}
  
  postcodeChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.postcodeChangedByUser(value, unsetDependents);
    }
  }  
  
  postcodeChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  townChanging(value: string, unsetDependents: boolean = true) {}
  
  townChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.townChangedByUser(value, unsetDependents);
    }
  }  
  
  townChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedByChanging(value: string, unsetDependents: boolean = true) {}
  
  updatedByChanged(value: string, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedByChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedByChangedByUser(value: string, unsetDependents: boolean = true) {
  }
  
  updatedDateChanging(value: Date, unsetDependents: boolean = true) {}
  
  updatedDateChanged(value: Date, unsetDependents: boolean = true) {
    const isUserChangeAfterPageLoad: boolean = !this.readOnly && this.isLifecycleStateItemSetComplete();
    if (isUserChangeAfterPageLoad) {
      this.updatedDateChangedByUser(value, unsetDependents);
    }
  }  
  
  updatedDateChangedByUser(value: Date, unsetDependents: boolean = true) {
  }
  

  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get firearmStorageLocationId() {
    return this.control('firearmStorageLocationId');
  }
  
  get businessUnit() {
    return this.control('businessUnit');
  }
  
  get addr1() {
    return this.control('addr1');
  }
  
  get addr2() {
    return this.control('addr2');
  }
  
  get addr3() {
    return this.control('addr3');
  }
  
  get county() {
    return this.control('county');
  }
  
  get createdBy() {
    return this.control('createdBy');
  }
  
  get createdDate() {
    return this.control('createdDate');
  }
  
  get description() {
    return this.control('description');
  }
  
  get disposalLocation() {
    return this.control('disposalLocation');
  }
  
  get email() {
    return this.control('email');
  }
  
  get location() {
    return this.control('location');
  }
  
  get locationType() {
    return this.control('locationType');
  }
  
  get lockVersion() {
    return this.control('lockVersion');
  }
  
  get postcode() {
    return this.control('postcode');
  }
  
  get town() {
    return this.control('town');
  }
  
  get updatedBy() {
    return this.control('updatedBy');
  }
  
  get updatedDate() {
    return this.control('updatedDate');
  }
  
}





